import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import './QuizGame.css';

const MultiplayerKBC = ({ 
  gameSession, 
  userRole, 
  userName, 
  userClass, 
  onExit 
}) => {
  const [socket, setSocket] = useState(null);
  const [gameState, setGameState] = useState('waiting');
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(30);
  const [showResult, setShowResult] = useState(false);
  const [showPrizeLadder, setShowPrizeLadder] = useState(false);
  const [scores, setScores] = useState({});
  const [opponentData, setOpponentData] = useState({});
  const [lifelines, setLifelines] = useState({
    fiftyFifty: true,
    skip: true,
    hint: true
  });
  const [opponentLifelines, setOpponentLifelines] = useState({
    fiftyFifty: true,
    skip: true,
    hint: true
  });

  const prizeAmounts = [100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 500000, 1000000];

  // Audio management system
  const [currentAudio, setCurrentAudio] = useState(null);
  const [timerAudio, setTimerAudio] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);

  // Stop current audio and play new one
  const playAudio = (audioFile) => {
    try {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      
      console.log('Playing audio:', audioFile);
      const audio = new Audio(`https://therisingschool.com/Learning%20Games/MP3/${audioFile}`);
      audio.volume = 0.5;
      setCurrentAudio(audio);
      
      audio.play().catch(error => console.log('Audio play failed:', error));
      audio.onended = () => setCurrentAudio(null);
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  };

  // Play timer audio specifically
  const startTimerAudio = () => {
    try {
      if (timerAudio) {
        timerAudio.pause();
        timerAudio.currentTime = 0;
      }
      
      const audio = new Audio('https://therisingschool.com/Learning%20Games/MP3/Timer.mp3');
      audio.volume = 0.5;
      audio.loop = true;
      setTimerAudio(audio);
      
      audio.play().catch(error => console.log('Timer audio play failed:', error));
    } catch (error) {
      console.log('Timer audio not supported:', error);
    }
  };

  // Stop timer audio immediately
  const stopTimerAudio = () => {
    if (timerAudio) {
      timerAudio.pause();
      timerAudio.currentTime = 0;
      setTimerAudio(null);
    }
  };

  // Stop current audio
  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    stopTimerAudio();
  };

  // Enhanced sound effects with MP3 files
  const playSound = (type) => {
    console.log('playSound called with type:', type);
    switch(type) {
      case 'start':
        if (!gameStarted) {
          console.log('Playing start sound, gameStarted:', gameStarted);
          playAudio('Start.mp3');
          setGameStarted(true);
        }
        break;
      case 'timer':
        console.log('Playing timer sound');
        startTimerAudio();
        break;
      case 'question':
        console.log('Playing question sound');
        stopTimerAudio(); // Stop timer immediately
        playAudio('Question.mp3');
        break;
      case 'lifeline':
        console.log('Playing lifeline sound');
        stopTimerAudio(); // Stop timer immediately
        playAudio('Question.mp3');
        break;
      default:
        // Fallback to original beep sounds for correct/wrong
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          if (type === 'correct') {
            oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.15);
            oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.3);
            gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.6);
          } else if (type === 'wrong') {
            oscillator.frequency.setValueAtTime(330, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(220, audioContext.currentTime + 0.2);
            oscillator.frequency.setValueAtTime(165, audioContext.currentTime + 0.4);
            gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.6);
          }
        } catch (error) {
          console.log('Audio not supported');
        }
    }
  };

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io();
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      newSocket.emit('join-game', gameSession.id);
      console.log('Joined game room:', gameSession.id);
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    // Listen for opponent updates
    newSocket.on('opponent-score-update', (data) => {
      console.log('Received opponent score update:', data);
      setOpponentData(prev => ({
        ...prev,
        score: data.score,
        questionIndex: data.questionIndex,
        isCorrect: data.isCorrect
      }));
      
      // Also update the scores state
      setScores(prev => ({
        ...prev,
        [data.playerName]: data.score
      }));
    });

    newSocket.on('opponent-lifeline-used', (data) => {
      console.log('Opponent used lifeline:', data);
      setOpponentLifelines(prev => ({
        ...prev,
        [data.lifeline]: false
      }));
    });

    newSocket.on('opponent-question-progress', (data) => {
      console.log('Opponent progressed to question:', data.questionIndex);
      setOpponentData(prev => ({
        ...prev,
        questionIndex: data.questionIndex
      }));
    });

    newSocket.on('game-state-updated', (data) => {
      setGameState(data.state);
      if (data.scores) setScores(data.scores);
      if (data.currentQuestion !== undefined) {
        setCurrentQuestion(data.currentQuestion);
        setTimeLeft(30);
        setSelectedAnswer('');
        setShowResult(false);
      }
    });

    return () => {
      newSocket.emit('leave-game', gameSession.id);
      newSocket.disconnect();
    };
  }, [gameSession.id]);

  // Load questions
  useEffect(() => {
    if (gameSession.questions && gameSession.questions.length >= 12) {
      setQuestions(gameSession.questions.slice(0, 12));
      setGameState('playing');
      
      // Initialize scores
      const initialScores = {};
      gameSession.players.forEach(player => {
        initialScores[player.name] = 0;
      });
      setScores(initialScores);
    }
  }, [gameSession]);

  // Timer with sound management
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      if (timeLeft === 30) {
        setTimeout(() => playSound('question'), 0);
      }
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      stopTimerAudio();
      handleTimeUp();
    }
  }, [timeLeft, gameState]);

  // Stop timer sound when answer is submitted
  useEffect(() => {
    if (showResult) {
      stopAudio();
    }
  }, [showResult]);

  // Play start sound when game begins (only once)
  useEffect(() => {
    if (gameState === 'playing' && questions.length > 0 && currentQuestion === 0 && !gameStarted) {
      playSound('start');
    }
  }, [gameState, questions.length, currentQuestion, gameStarted]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  const submitAnswer = () => {
    // Stop timer audio immediately when Final Answer is clicked
    stopTimerAudio();
    
    const correct = selectedAnswer === questions[currentQuestion].correctAnswer;
    const points = correct ? prizeAmounts[currentQuestion] : 0;
    
    // Update local score
    const newScores = { ...scores };
    newScores[userName] = (newScores[userName] || 0) + points;
    setScores(newScores);

    // Emit score update to opponent
    if (socket) {
      console.log('Emitting score update:', {
        gameId: gameSession.id,
        playerName: userName,
        score: newScores[userName],
        questionIndex: currentQuestion,
        isCorrect: correct
      });
      socket.emit('score-update', {
        gameId: gameSession.id,
        playerName: userName,
        score: newScores[userName],
        questionIndex: currentQuestion,
        isCorrect: correct,
        timeSpent: 30 - timeLeft
      });
    }

    setShowResult(true);
    setTimeout(() => {
      if (correct && currentQuestion < questions.length - 1) {
        setShowPrizeLadder(true);
      } else if (currentQuestion < questions.length - 1) {
        const nextQuestion = currentQuestion + 1;
        setCurrentQuestion(nextQuestion);
        setSelectedAnswer('');
        setShowResult(false);
        setTimeLeft(30);
        
        setTimeout(() => playSound('question'), 0);
        
        if (socket) {
          socket.emit('question-progress', {
            gameId: gameSession.id,
            playerName: userName,
            questionIndex: nextQuestion
          });
        }
      } else {
        endGame();
      }
    }, 2000);
  };

  const handleTimeUp = () => {
    setShowResult(true);
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        const nextQuestion = currentQuestion + 1;
        setCurrentQuestion(nextQuestion);
        setSelectedAnswer('');
        setShowResult(false);
        setTimeLeft(30);
        
        setTimeout(() => playSound('question'), 0);
        setTimeout(() => startTimerAudio(), 100);
      } else {
        endGame();
      }
    }, 2000);
  };

  const endGame = () => {
    setGameState('finished');
    if (socket) {
      socket.emit('game-state-change', {
        gameId: gameSession.id,
        state: 'finished',
        scores
      });
    }
  };

  const useFiftyFifty = () => {
    if (!lifelines.fiftyFifty) return;
    
    playSound('lifeline');
    
    const currentQ = questions[currentQuestion];
    const wrongAnswers = currentQ.options.filter(opt => opt !== currentQ.correctAnswer);
    const toRemove = wrongAnswers.slice(0, 2);
    
    setQuestions(prev => {
      const updated = [...prev];
      updated[currentQuestion] = {
        ...updated[currentQuestion],
        options: updated[currentQuestion].options.filter(opt => !toRemove.includes(opt))
      };
      return updated;
    });
    
    setLifelines(prev => ({ ...prev, fiftyFifty: false }));
    
    // Emit lifeline usage to opponent
    if (socket) {
      socket.emit('lifeline-used', {
        gameId: gameSession.id,
        playerName: userName,
        lifeline: 'fiftyFifty'
      });
    }
  };

  const useSkip = () => {
    if (!lifelines.skip || currentQuestion >= questions.length - 1) return;
    
    playSound('lifeline');
    
    setCurrentQuestion(currentQuestion + 1);
    setSelectedAnswer('');
    setTimeLeft(30);
    setLifelines(prev => ({ ...prev, skip: false }));
    
    if (socket) {
      socket.emit('lifeline-used', {
        gameId: gameSession.id,
        playerName: userName,
        lifeline: 'skip'
      });
    }
  };

  const useHint = () => {
    if (!lifelines.hint) return;
    
    playSound('lifeline');
    alert(questions[currentQuestion].hint || "No hint available for this question");
    setLifelines(prev => ({ ...prev, hint: false }));
    
    
    if (socket) {
      socket.emit('lifeline-used', {
        gameId: gameSession.id,
        playerName: userName,
        lifeline: 'hint'
      });
    }
  };

  if (gameState === 'waiting') {
    return (
      <div className="quiz-game">
        <div className="prize-ladder-container">
          <h2>🎮 Multiplayer KBC</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div>Waiting for game to start...</div>
            <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              Players: {gameSession.players.map(p => p.name).join(' vs ')}
            </div>
          </div>
          <button onClick={() => { stopAudio(); onExit(); }} className="quit-btn">Exit Game</button>
        </div>
      </div>
    );
  }

  if (showPrizeLadder) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #6B46C1 0%, #9333EA 50%, #7C3AED 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Spotlight effects */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '20%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'pulse 2s infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'pulse 3s infinite'
        }}></div>
        
        <div style={{
          background: 'rgba(0,0,0,0.8)',
          padding: '30px',
          borderRadius: '20px',
          border: '3px solid gold',
          boxShadow: '0 0 30px rgba(255,215,0,0.5)',
          maxWidth: '400px',
          width: '90%'
        }}>
          <h2 style={{ color: 'gold', textAlign: 'center', marginBottom: '20px' }}>🏆 Prize Ladder</h2>
          
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {prizeAmounts.map((amount, index) => {
              const isCurrentLevel = index === currentQuestion;
              const isTopPrize = index === prizeAmounts.length - 1;
              const isCompleted = index < currentQuestion;
              
              return (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 15px',
                  margin: '5px 0',
                  borderRadius: '10px',
                  background: isCurrentLevel ? 'linear-gradient(90deg, #10B981, #059669)' :
                             isTopPrize ? 'linear-gradient(90deg, #FFD700, #FFA500)' :
                             isCompleted ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255,255,255,0.1)',
                  border: isCurrentLevel ? '2px solid #10B981' :
                         isTopPrize ? '2px solid gold' : '1px solid rgba(255,255,255,0.2)',
                  color: isTopPrize ? '#000' : '#fff',
                  fontWeight: isCurrentLevel || isTopPrize ? 'bold' : 'normal',
                  fontSize: isTopPrize ? '18px' : '16px',
                  boxShadow: isCurrentLevel ? '0 0 15px rgba(16, 185, 129, 0.5)' :
                            isTopPrize ? '0 0 15px rgba(255, 215, 0, 0.5)' : 'none',
                  transform: isCurrentLevel ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.3s ease'
                }}>
                  <span>{index + 1}.</span>
                  <span>{amount.toLocaleString()} Points</span>
                  {isCurrentLevel && <span>👈 YOU ARE HERE</span>}
                  {isTopPrize && <span>👑</span>}
                  {isCompleted && <span>✅</span>}
                </div>
              );
            })}
          </div>
          
          <button 
            onClick={() => {
              setShowPrizeLadder(false);
              const nextQuestion = currentQuestion + 1;
              setCurrentQuestion(nextQuestion);
              setSelectedAnswer('');
              setShowResult(false);
              setTimeLeft(30);
              
              setTimeout(() => playSound('question'), 0);
              setTimeout(() => startTimerAudio(), 100);
              
              if (socket) {
                socket.emit('question-progress', {
                  gameId: gameSession.id,
                  playerName: userName,
                  questionIndex: nextQuestion
                });
              }
            }}
            style={{
              width: '100%',
              padding: '15px',
              marginTop: '20px',
              background: 'linear-gradient(90deg, #10B981, #059669)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
            }}
          >
            👆 Tap to Continue
          </button>
        </div>
        
        <style>{`
          @keyframes pulse {
            0% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.1); }
            100% { opacity: 0.3; transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  if (gameState === 'finished') {
    const winner = Object.keys(scores).reduce((a, b) => scores[a] > scores[b] ? a : b);
    
    return (
      <div className="quiz-game">
        <div className="prize-ladder-container">
          <h2>🏆 Game Over!</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <h3>Final Scores:</h3>
            {Object.entries(scores).map(([player, score]) => (
              <div key={player} style={{ 
                margin: '10px 0', 
                fontSize: '18px',
                fontWeight: player === winner ? 'bold' : 'normal',
                color: player === winner ? '#10b981' : '#000'
              }}>
                {player}: {score.toLocaleString()} Points
                {player === winner && ' 🏆'}
              </div>
            ))}
          </div>
          <button onClick={() => { stopAudio(); onExit(); }} className="continue-btn">Back to Games</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  if (!currentQ) return <div>Loading...</div>;

  return (
    <div className="quiz-game">
      {/* Both Players Dashboard - Mobile Responsive */}
      <div className="players-dashboard" style={{
        position: 'fixed',
        top: window.innerWidth < 768 ? 'auto' : '10px',
        bottom: window.innerWidth < 768 ? '10px' : 'auto',
        right: window.innerWidth < 768 ? '5px' : '10px',
        left: window.innerWidth < 768 ? '5px' : 'auto',
        background: 'rgba(0,0,0,0.95)',
        color: 'white',
        padding: window.innerWidth < 768 ? '8px' : '15px',
        borderRadius: '8px',
        fontSize: window.innerWidth < 768 ? '10px' : '14px',
        zIndex: 1000,
        width: window.innerWidth < 768 ? 'auto' : '250px',
        border: '2px solid #10b981',
        maxHeight: window.innerWidth < 768 ? '120px' : 'auto',
        overflowY: 'auto'
      }}>
        <div style={{ 
          fontWeight: 'bold', 
          marginBottom: window.innerWidth < 768 ? '5px' : '10px', 
          color: '#10b981', 
          textAlign: 'center',
          fontSize: window.innerWidth < 768 ? '11px' : '14px'
        }}>🏆 Live Scores</div>
        
        {window.innerWidth < 768 ? (
          /* Mobile: Horizontal Layout */
          <div style={{ display: 'flex', gap: '8px', marginBottom: '5px' }}>
            <div style={{ flex: 1, padding: '4px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '4px' }}>
              <div style={{ fontSize: '9px', color: '#10b981' }}>YOU</div>
              <div style={{ fontSize: '11px', fontWeight: 'bold' }}>💰 {scores[userName] || 0}</div>
            </div>
            <div style={{ flex: 1, padding: '4px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '4px' }}>
              <div style={{ fontSize: '9px', color: '#ef4444' }}>OPP</div>
              <div style={{ fontSize: '11px', fontWeight: 'bold' }}>💰 {scores[gameSession.players.find(p => p.name !== userName)?.name] || 0}</div>
            </div>
          </div>
        ) : (
          /* Desktop: Vertical Layout */
          <>
            <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '5px' }}>
              <div style={{ fontSize: '12px', color: '#10b981' }}>YOU</div>
              <div style={{ fontWeight: 'bold', fontSize: '14px' }}>👤 {userName}</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981' }}>💰 {scores[userName] || 0}</div>
              <div style={{ fontSize: '12px' }}>📝 Q: {currentQuestion + 1}/12</div>
            </div>
            <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '5px' }}>
              <div style={{ fontSize: '12px', color: '#ef4444' }}>OPPONENT</div>
              <div style={{ fontWeight: 'bold', fontSize: '14px' }}>👤 {gameSession.players.find(p => p.name !== userName)?.name || 'Unknown'}</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ef4444' }}>💰 {scores[gameSession.players.find(p => p.name !== userName)?.name] || 0}</div>
              <div style={{ fontSize: '12px' }}>📝 Q: {(opponentData.questionIndex || 0) + 1}/12</div>
            </div>
          </>
        )}
        
        {/* Lifelines Comparison */}
        {window.innerWidth < 768 ? (
          /* Mobile: Ultra Compact Lifelines */
          <div style={{ fontSize: '8px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#10b981' }}>You: {lifelines.fiftyFifty ? '✅' : '❌'} {lifelines.skip ? '✅' : '❌'} {lifelines.hint ? '✅' : '❌'}</span>
            <span style={{ color: '#ef4444' }}>Opp: {opponentLifelines.fiftyFifty ? '✅' : '❌'} {opponentLifelines.skip ? '✅' : '❌'} {opponentLifelines.hint ? '✅' : '❌'}</span>
          </div>
        ) : (
          /* Desktop: Full Lifelines */
          <div style={{ fontSize: '12px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>🎯 Lifelines:</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#10b981', fontSize: '10px' }}>You:</div>
                <div style={{ fontSize: '10px' }}>50:50 {lifelines.fiftyFifty ? '✅' : '❌'}</div>
                <div style={{ fontSize: '10px' }}>Skip {lifelines.skip ? '✅' : '❌'}</div>
                <div style={{ fontSize: '10px' }}>Hint {lifelines.hint ? '✅' : '❌'}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#ef4444', fontSize: '10px' }}>Them:</div>
                <div style={{ fontSize: '10px' }}>50:50 {opponentLifelines.fiftyFifty ? '✅' : '❌'}</div>
                <div style={{ fontSize: '10px' }}>Skip {opponentLifelines.skip ? '✅' : '❌'}</div>
                <div style={{ fontSize: '10px' }}>Hint {opponentLifelines.hint ? '✅' : '❌'}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="game-header">
        <div className="points-display">
          <div className="coin-animation">
            🪙 {scores[userName] || 0} Points
          </div>
        </div>
        
        <div className="timer-circle">
          <div className="timer-inner">
            <span>{timeLeft}</span>
          </div>
        </div>
        
        <div className="question-counter">
          {currentQuestion + 1}/{questions.length}
        </div>
      </div>

      <div className="question-section">
        <div className="question-box">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>🎯 Same Question for Both Players</span>
            <span style={{ fontSize: '14px', color: '#10b981', fontWeight: 'bold' }}>⚡ LIVE MATCH</span>
          </div>
          <h3>{currentQ.question}</h3>
        </div>
        
        <div className="options-grid">
          {currentQ.options.map((option, index) => (
            <button
              key={index}
              className={`option-btn ${selectedAnswer === option ? 'selected' : ''} ${
                showResult ? (option === currentQ.correctAnswer ? 'correct' : 
                selectedAnswer === option ? 'wrong' : '') : ''
              }`}
              onClick={() => handleAnswerSelect(option)}
              disabled={showResult}
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              <span className="option-text">{option}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="game-controls">
        <div className="lifelines">
          <button 
            className={`lifeline-btn ${!lifelines.fiftyFifty ? 'used' : ''}`}
            onClick={useFiftyFifty}
            disabled={!lifelines.fiftyFifty}
          >
            50:50
          </button>
          <button 
            className={`lifeline-btn ${!lifelines.skip ? 'used' : ''}`}
            onClick={useSkip}
            disabled={!lifelines.skip}
          >
            Skip
          </button>
          <button 
            className={`lifeline-btn ${!lifelines.hint ? 'used' : ''}`}
            onClick={useHint}
            disabled={!lifelines.hint}
          >
            Hint
          </button>
        </div>
        
        <div className="action-buttons">
          <button 
            className="submit-btn"
            onClick={submitAnswer}
            disabled={!selectedAnswer || showResult}
          >
            Final Answer
          </button>
          <button className="quit-btn" onClick={() => { stopAudio(); onExit(); }}>Quit Game</button>
        </div>
      </div>
    </div>
  );
};

export default MultiplayerKBC;